<script setup lang="ts">
import type { ColumnFiltersState } from '@tanstack/vue-table'
import TableRowElementalItem from '../TableRowElementalItem/index.vue'
import TableRowElementalAccordeon from '../TableRowAccordeonElementalsList/index.vue'
import TableRowDynamic from '../TableRowDynamic/index.vue'
import TableRowSkeleton from '../TableRowSkeleton/index.vue'
import TableRowElemental from './index.vue'
import { useRemoveSaveElementalInAList } from '~~/src/composables/mutations/elemetal/useRemoveSaveElementalInAList'
import { useSaveElementalInAList } from '~~/src/composables/mutations/elemetal/useSaveElementalInAList'
import { useListElementalsQuery } from '~~/src/composables/query/useListElementalsQuery'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'
import type { List } from '~~/src/types/list'
import type { Elemental } from '~~/src/types/elemental'
import { useSidebarStore } from '~~/src/stores/sidebar'
import { ElementalListContext, useElementalListContext } from '~~/src/components/DrawerSidebarRight/components/shared/ElementalList/ElementalListContext'
import DraggableElement from '@/components/utils/DragAndDrop/DraggableElement.vue'
import { useUpdateListItems } from '~~/src/composables/mutations/elemetal/useUpdateListItems'
import { useRemoveSaveElemental } from '~~/src/composables/mutations/elemetal/useRemoveSaveElemental'

const props = defineProps<{
  title?: string
  list: Elemental | List
  elementalLabel?: string
  level?: number
  dataIndex?: number
  cellsLength?: number
  columns: string[]
  filters?: ColumnFiltersState
}>()

const toast = useToast()
const user = useUser()
const listId = props.list.id
const { isElementalOpen } = useSidebarStore()

const { listId: parentListId } = useElementalListContext()
const isOpen = isElementalOpen(listId, parentListId
  ? `${parentListId}`
  : null)

const isOwner = computed(() => {
  return user.value?.id === props.list.user.id
})

const { data: elementals, refetch, isLoading, isError, error } = useListElementalsQuery(
  {
    id: props.list.id,
    query: {
      size: 100,
    },
  }, {
    enabled: isOpen,
    retry: 2,
  },
)

const { mutate: saveElementalInAListMutate, isPending: isPendingSaveElemental } = useSaveElementalInAList()
const { mutate: removeSaveElementalInAListMutate, isPending: isPendingRemoveSaveElementalInAList } = useRemoveSaveElementalInAList()
const { mutate: removeSaveElementalMutate, isPending: isPendingRemoveSaveElemental } = useRemoveSaveElemental()

const { state, countList } = useElementalListsStore()

const localElementals = ref<Elemental[]>([])

watch(elementals, (val) => {
  localElementals.value = val ? [...val] : []
}, { immediate: true })

const { filteredElementals, showList } = useElementalListFilter(
  computed(() => ({
    list: props.list,
    elementals: localElementals.value ?? [],
  })),
  computed(() => state.query),
  computed(() => props.filters ?? []),
  countList,
)

const { mutate } = useUpdateListItems()

const moveElemental = (from: number, to: number) => {
  if (!elementals.value) {
    toast.error('No items')
    return
  }
  if (from === to) {
    toast.error('From and to are the same')
    return
  }

  const movedItem = localElementals.value.splice(from, 1)[0]
  localElementals.value.splice(to, 0, movedItem)

  const items = localElementals.value?.map(item => item.id) ?? []

  mutate({
    id: props.list.id,
    itemIds: items,
    contentTypeId: props.list.contentTypeId,
  }, {
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })
}

onUnmounted(() => {
  const list = props.list
  countList(list.id, list.elementalTypeId!, 0)
})

const validDrop = (data: Elemental) => {
  if (user.value?.id !== props.list.user.id) {
    toast.error('You are not the owner of this list')
    return false
  }
  if (data.isList && data.id === listId) {
    toast.error('You cannot drop a list inside itself')
    return false
  }
  return true
}

const onDrop = (data: { elemental: Elemental; listId?: string }, index: number) => {
  const elemental = data.elemental

  if (!validDrop(elemental))
    return

  const elementalParent = data.listId

  if (elementalParent) {
    removeSaveElementalInAListMutate({
      id: elemental.id,
      listId: elementalParent,
      elementalType: elemental.elementalTypeId!,
    }, {
      onError: (error) => {
        toast.error(error.message)
      },
    })
  }

  if (!elementalParent) {
    removeSaveElementalMutate({
      elementalType: elemental.elementalTypeId!,
      id: elemental.id,
    }, {
      onError: (error) => {
        toast.error(error.message)
      },
    })
  }

  if (!elementals.value) {
    saveElementalInAListMutate({
      id: elemental.id,
      elementalType: elemental.elementalTypeId!,
      listId: props.list.id,
      orderIndex: index >= 0 ? index : undefined,
    }, {
      onSuccess: () => {
        refetch()
      },
      onError: (error) => {
        toast.error(error.message)
      },
    })
    return
  }

  const isOnList = elementals.value?.some(el => el.id === elemental.id)

  if (isOnList) {
    toast.error('Item already on list')
    return
  }

  const items = elementals.value.map(item => item.id)

  const newElemental = elemental.id
  const newElementals = insertAtBefore(items, index, newElemental)

  mutate({
    id: props.list.id,
    itemIds: newElementals,
  }, {
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })
}

const isLoadingList = computed(() => {
  return isLoading.value || isPendingSaveElemental.value || isPendingRemoveSaveElemental.value || isPendingRemoveSaveElementalInAList.value
})

provide(ElementalListContext, {
  listId,
  onList: true,
  isOwner,
  parentListId,
  isFolder: true,
  isAll: false,
})
const folderLevel = props.level ? props.level + 1 : 1
</script>

<template>
  <!-- print level in class name -->
  <div v-if="showList" class="list-elemental" :class="[{ 'border-b-1 border-grayLighter': !isOpen }]">
    <DraggableElement
      :is="folderLevel === 1 ? 'div' : 'li'"
      class="list-none"
      :data="{
        listId: parentListId,
        elemental: list,
      }"
      :text-content="list.template ?? list.title"
      :data-index="dataIndex"
    >
      <!-- HEADER -->
      <TableRowElementalAccordeon
        v-show="showList"
        :folder-level="folderLevel"
        :list="list"
        :list-id="listId"
        :is-main="false"
        :parent-list-id="parentListId"
        :list-title="title ?? list.title"
        :n-prompts="filteredElementals ? filteredElementals.length : list.nPrompts ?? 0"
        :elementals-type="list.elementalTypeId"
        class="border-none !m-0 border-grayLighter"
        @moved="moveElemental"
        @droped="onDrop"
        @on-toggle="(value) => value && refetch()"
      >
        <template #header>
          <TableRowDynamic
            :list="list"
            :folder-level="folderLevel"
            :columns="columns"
            :elemental-label="elementalLabel ?? ''"
            :class="[`level-${folderLevel}`]"
          >
            <template #list-icon>
              <slot name="list-icon" />
            </template>
          </TableRowDynamic>
        </template>

        <template #content>
          <template v-if="!isLoadingList">
            <template v-for="(elemental, index) in filteredElementals" :key="`table-row-elemental-${elemental.id}-${index}`">
              <template v-if="list.title === 'all'" />
              <TableRowElementalItem
                v-if="!elemental.isList || folderLevel + 1 >= 3"
                :elemental="elemental"
                :data-index="index"
                :elemental-label="elementalLabel"
                :folder-level="folderLevel + 1"
                :columns="columns"
              />
              <TableRowElemental
                v-else
                :list="elemental"
                :level="folderLevel"
                :is-main="false"
                :elementals-type="elemental.elementalTypeId"
                :cells-length="cellsLength"
                :data-index="index"
                :elemental-label="elementalLabel"
                :columns="columns"
                :filters="filters"
              />
            </template>
            <template v-if="(!filteredElementals || filteredElementals.length === 0)">
              <div class="text-darkGray bg-[#F6F6FF]  text-sm text-center p-4">
                {{ isError ? error?.message : 'No items' }}
              </div>
            </template>
          </template>

          <div v-if="isLoadingList" class="flex flex-col gap-.5">
            <template v-for="(_, index) in Array(Math.max(Math.min(5, list.nPrompts ?? 3), 3))" :key="`${index}-skeleton-${folderLevel}`">
              <TableRowSkeleton :columns="columns" :folder-level="folderLevel + 1" />
            </template>
          </div>
        </template>
      </TableRowElementalAccordeon>
    </DraggableElement>
  </div>
</template>

<style lang="scss" scoped>
</style>
